import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ProfileCard,
  type ProfileCardContract,
  type ProfileSurface,
} from '../../../../app/_shared/educationProfile';
import type {
  MentorProfileStepId,
  MentorProfileStepProgress,
} from '../../application/mentorProfileSteps';
import { MentorProfileSteps } from './MentorProfileSteps';

type MentorProfileSummaryProps = {
  surface: Extract<ProfileSurface, 'edit' | 'pre-edit'>;
  displayName: string;
  timezoneLabel: string;
  percent: number;
  steps: MentorProfileStepProgress[];
  activeStepId: MentorProfileStepId;
  onStepSelect: (id: MentorProfileStepId) => void;
  onContinue: () => void;
  picture: ReactNode;
  continueLabel?: string;
};

export function MentorProfileSummary({
  surface,
  displayName,
  timezoneLabel,
  percent,
  steps,
  activeStepId,
  onStepSelect,
  onContinue,
  picture,
  continueLabel,
}: MentorProfileSummaryProps) {
  const { t } = useTranslation();
  const isComplete = percent >= 100;
  const contract: ProfileCardContract = {
    surface,
    identity: {
      displayName,
      timezoneLabel,
      title: t('dashboard.profileCard.mentor_title'),
      picture,
    },
    progress: {
      percent,
      hint: isComplete ? null : t('dashboard.profileCard.mentor_hint'),
      cta: {
        label:
          continueLabel ||
          (isComplete
            ? t('dashboard.profileCard.cta_edit')
            : t('mentor-profile.continue')),
        primary: !isComplete,
        onClick: onContinue,
      },
    },
  };

  return (
    <>
      <ProfileCard contract={contract} />
      <MentorProfileSteps
        steps={steps}
        activeStepId={activeStepId}
        onStepSelect={onStepSelect}
      />
    </>
  );
}

export default MentorProfileSummary;
